import maplibregl from 'maplibre-gl';
import { Element_Base } from '~/common/typescript/models/Element';
import { renderToString } from 'react-dom/server';
import { PiMapPin } from 'react-icons/pi';

export const ElementLabelOverlay = {
  private_labelOverlays: new Map<string, HTMLDivElement>(), // elementUuid -> div element

  /**
   * Create HTML div overlays for element labels
   */
  createLabelOverlays(elements: Element_Base[]) {
    if (!window.map) return;

    // Clear existing overlays first
    this.removeLabelOverlays();

    elements.forEach((element) => {
      if (!element.geom || !element.name) return;

      try {
        let geomObj = element.geom;
        if (typeof element.geom === 'string') {
          geomObj = JSON.parse(element.geom);
        }

        let coordinates: [number, number] | null = null;

        // Handle Point geometry
        if (geomObj.type === 'Point') {
          coordinates = geomObj.coordinates;
        }
        // Handle other geometries by getting centroid
        else if (geomObj.type === 'LineString' && geomObj.coordinates.length > 0) {
          // Use midpoint of LineString
          const coords = geomObj.coordinates;
          const midIndex = Math.floor(coords.length / 2);
          coordinates = coords[midIndex];
        }
        else if (geomObj.type === 'Polygon' && geomObj.coordinates.length > 0) {
          // Use first coordinate of polygon (simple approximation)
          coordinates = geomObj.coordinates[0][0];
        }

        if (coordinates) {
          // Create label div
          const labelDiv = document.createElement('div');
          labelDiv.className = 'element-label-overlay';
          labelDiv.innerHTML = `${renderToString(<PiMapPin style={{ verticalAlign: '-1px', marginRight: 4, color: '#1677ff' }} />)}${element.name}`;
          labelDiv.style.cssText = `
            background: rgba(255, 255, 255, 0.9);
            border: 1px solid #ccc;
            border-radius: 4px;
            padding: 2px 6px;
            font-size: 12px;
            color: #333;
            white-space: nowrap;
            pointer-events: none;
            box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          `;

          // Create MapLibre marker with the div
          const marker = new maplibregl.Marker({
            element: labelDiv,
            anchor: 'left',
            offset: [15, 0] // Offset to the right of the element icon
          })
          .setLngLat(coordinates)
          .addTo(window.map);

          // Store both div and marker for cleanup
          this.private_labelOverlays.set(element.uuid!, labelDiv);
          (labelDiv as any)._marker = marker;
        }
      } catch (error) {
        console.error('Error creating element label overlay:', error);
      }
    });
  },

  /**
   * Remove all label overlays
   */
  removeLabelOverlays() {
    this.private_labelOverlays.forEach((labelDiv, elementUuid) => {
      const marker = (labelDiv as any)._marker;
      if (marker) {
        marker.remove();
      }
    });
    this.private_labelOverlays.clear();
  },

  /**
   * Update specific element label
   */
  updateElementLabel(element: Element_Base) {
    if (!window.map) return;

    // Remove existing label for this element
    const existingDiv = this.private_labelOverlays.get(element.uuid!);
    if (existingDiv) {
      const marker = (existingDiv as any)._marker;
      if (marker) {
        marker.remove();
      }
      this.private_labelOverlays.delete(element.uuid!);
    }

    // Create new label if element has name and geometry
    if (element.name && element.geom) {
      this.createLabelOverlays([element]);
    }
  },

  /**
   * Update labels visibility
   */
  updateLabelsVisibility(elements: Element_Base[], showLabels: boolean) {
    if (showLabels) {
      this.createLabelOverlays(elements);
    } else {
      this.removeLabelOverlays();
    }
  }
};